import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useMeta } from "@/hooks/useMeta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { rupiah } from "@/lib/format";

const DEFAULT_COMPANY = {
  name: "ADARI", tagline: "CONNECTING NEEDS AND WANTS",
  subtitle: "INTERIOR & EXTERIOR DESIGN & PLANNING CONSULTANT",
  address: "Rukan Graha Kencana Blok A. Jl. Perjuangan No.99, Kel. Kebon Jeruk, Kec. Kebon Jeruk, Kota Jakarta Barat, 11530",
  email: "pt.pusatmasadepan@gmail.com", phone: "0821-8064-9700", logo_url: "",
};
const DEFAULT_PAYMENT = { account: "PT. PUSAT MASA DEPAN", bank: "MANDIRI", acc_no: "1130022201622", status: "BELUM LUNAS" };
const blankItem = () => ({ description: "", volume: 0, unit: "M2", price: 0, amount: "" });

const GARANSI_PRESETS = [30, 60, 90, 180, 365];
// Reference-only suggestion, not an enforced rule — the actual garansi_hari value stays
// fully manual (Select/Custom above); this just gives a starting point tied to contract size.
const suggestedGaransiLabel = (total) => {
  if (total < 100_000_000) return "30 hari";
  if (total < 300_000_000) return "60 hari";
  if (total < 500_000_000) return "90 hari (3 bulan)";
  if (total < 1_000_000_000) return "6 bulan";
  return "12 bulan";
};
const AUTO_SECTION_KINDS = ["biaya", "biaya_termin", "jangka_waktu", "nilai_ppn", "garansi", "lingkup_produksi", "masa_persiapan"];
const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const fmtIdDate = (iso) => {
  if (!iso) return "-";
  const dt = new Date(iso + "T00:00:00");
  return `${dt.getDate()} ${BULAN_ID[dt.getMonth()]} ${dt.getFullYear()}`;
};
// Renders the actual date-to-date span for a duration in days (e.g. "1 - 30 Juni 2026")
// so the contract always shows real dates, not just a floating day count.
const fmtDurationRange = (startIso, days) => {
  if (!startIso || !days) return "-";
  const start = new Date(startIso + "T00:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + Number(days) - 1);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  return sameMonth
    ? `${start.getDate()} - ${end.getDate()} ${BULAN_ID[end.getMonth()]} ${end.getFullYear()} (${days} hari)`
    : `${fmtIdDate(startIso)} - ${fmtIdDate(end.toISOString().slice(0, 10))} (${days} hari)`;
};
// Shifts an ISO date forward by n days — used to derive the physical-work start date
// (Masa Persiapan start + Masa Persiapan length) from a single user-entered start date.
const addDaysIso = (iso, n) => {
  if (!iso) return iso;
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + Number(n || 0));
  return d.toISOString().slice(0, 10);
};

// Status bayar invoice dihitung dari data pembayaran ASLI di lead (bukan diketik manual),
// supaya tidak pernah beda dengan kondisi piutang sebenarnya — dibaca dari ledger Design
// atau Produksi sesuai revenue_kind dokumen, karena keduanya independen.
const computeInvoiceStatus = (lead, kind = "design") => {
  if (!lead) return "BELUM LUNAS";
  if (kind === "produksi") {
    if (!lead.produksi_closing_date) return "BELUM LUNAS";
    const nilai = lead.nilai_produksi || 0;
    const paid = lead.produksi_paid_total || 0;
    if (nilai > 0 && paid >= nilai) return "LUNAS";
    if (paid > 0) return "SEBAGIAN";
    return "BELUM LUNAS";
  }
  if (lead.status !== "Closing") return "BELUM LUNAS";
  const nilai = lead.nilai_deal || 0;
  const paid = lead.paid_total || 0;
  if (nilai > 0 && paid >= nilai) return "LUNAS";
  if (paid > 0) return "SEBAGIAN";
  return "BELUM LUNAS";
};

export default function DocumentDialog({ open, onOpenChange, lead, doc, onSaved }) {
  const qc = useQueryClient();
  const { companyAddresses, companySignatures, bankAccounts, documentTypes, master } = useMeta();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  // Discount/Tax are core to Invoice & SPH, but rarely used in a Kontrak (its value
  // is the lump-sum RAB total) — collapsed by default there so the form isn't cluttered
  // with fields that mostly stay at 0, still reachable in one click when actually needed.
  const [showDiscountTax, setShowDiscountTax] = useState(false);
  // Tracks "user explicitly picked Custom/Manual" separately from the numeric value —
  // garansi_hari alone can't tell them apart (a custom value can happen to equal a preset,
  // e.g. still 30 right after switching), which used to make the custom input never appear:
  // picking "custom" left garansi_hari unchanged at a preset number, so the field stayed
  // hidden and there was no way to actually type a custom day count.
  const [garansiCustom, setGaransiCustom] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      if (doc) {
        const status = doc.type === "invoice" ? computeInvoiceStatus(lead, doc.revenue_kind || "design") : (doc.payment?.status || "BELUM LUNAS");
        setF({
          ...doc, revenue_kind: doc.revenue_kind || "design", payment: { ...doc.payment, status },
          items: doc.items?.length ? doc.items.map((it) => ({ description: it.description, volume: it.volume ?? 0, unit: it.unit || "M2", price: it.price ?? 0, amount: it.amount ?? "" })) : [blankItem()],
          termins: (doc.termins || []).map((t) => ({ ...t, note: t.note || "" })), tax: doc.tax ?? 0, sections: doc.sections || [], duration_text: doc.duration_text || "",
          duration_days: doc.duration_days || 0, duration_start_date: doc.duration_start_date || "", title: doc.title || "",
          masa_persiapan_hari: doc.masa_persiapan_hari || 0,
          address_id: doc.address_id || "", signature_id: doc.signature_id || "", bank_account_ids: doc.bank_account_ids || [],
          kategori_proyek: doc.kategori_proyek || "", event_tanggal_pelaksanaan: doc.event_tanggal_pelaksanaan || "", event_tanggal_bongkar: doc.event_tanggal_bongkar || "",
          kena_ppn: doc.kena_ppn || false, denda_pct_per_hari: doc.denda_pct_per_hari ?? 0.1, denda_maks_pct: doc.denda_maks_pct ?? 5, toleransi_hari: doc.toleransi_hari ?? 3,
          garansi_hari: doc.garansi_hari ?? 30, nik_klien: doc.nik_klien || "", head_designer: doc.head_designer || "", project_manager: doc.project_manager || "",
          sertakan_rincian_rab: doc.sertakan_rincian_rab ?? true,
        });
        return;
      }
      let number = "";
      let sections = [];
      try { number = (await api.get(`/documents/next-number?type=invoice`)).data.number; } catch { /* noop */ }
      try { sections = (await api.get(`/settings/quotation-sections`)).data; } catch { /* noop */ }
      setF({
        type: "invoice", revenue_kind: "design", number, date: new Date().toISOString().slice(0, 10),
        company: { ...DEFAULT_COMPANY }, cakupan: "full", duration_text: "", duration_days: 0, duration_start_date: "", title: "",
        masa_persiapan_hari: 0,
        client: { nama: lead?.nama || "", alamat: lead?.kota || "", telp: lead?.no_hp || "", email: "" },
        items: [blankItem()], discount_pct: 0, tax: 0, termins: [], sections,
        payment: { ...DEFAULT_PAYMENT, status: computeInvoiceStatus(lead) }, terms: [], notes: "",
        signer_name: "", signer_title: "Manager Keuangan", signature_url: "",
        address_id: "", signature_id: "", bank_account_ids: [],
        kategori_proyek: lead?.kategori_customer || "", event_tanggal_pelaksanaan: "", event_tanggal_bongkar: "",
        kena_ppn: false, denda_pct_per_hari: 0.1, denda_maks_pct: 5, toleransi_hari: 3,
        garansi_hari: 30, nik_klien: "", head_designer: "", project_manager: "",
        sertakan_rincian_rab: true,
      });
    })();
  }, [open, doc, lead]);

  // Keeps Tax(Rp) in sync with 11% of the current subtotal whenever Kena PPN is on and
  // items change — without this, checking the box once would freeze the tax amount even
  // as the admin keeps editing item prices/volumes afterward.
  useEffect(() => {
    if (!f || f.type !== "kontrak_produksi" || !f.kena_ppn) return;
    const sub = (f.items || []).reduce((a, it) => {
      const amt = it.amount !== "" && it.amount != null ? Number(it.amount) : Number(it.volume || 0) * Number(it.price || 0);
      return a + amt;
    }, 0);
    const expected = Math.round(sub * 0.11);
    if (f.tax !== expected) setF((s) => ({ ...s, tax: expected }));
  }, [f]);

  const changeType = async (type) => {
    let number = f.number;
    if (!doc) { try { number = (await api.get(`/documents/next-number?type=${type}`)).data.number; } catch { /* noop */ } }
    const status = type === "invoice" ? computeInvoiceStatus(lead, f.revenue_kind) : f.payment.status;
    let sections = f.sections;
    // Switching between section-based types (or from a non-sectioned type into one)
    // fetches that type's own defaults fresh — only on create, never touching an
    // existing document's already-saved sections when just editing metadata.
    if (!doc && type === "quotation") {
      try { sections = (await api.get("/settings/quotation-sections")).data; } catch { /* noop */ }
    } else if (!doc && type === "kontrak_design") {
      try { sections = (await api.get(`/settings/kontrak-design-sections?variant=${f.cakupan || "full"}`)).data; } catch { /* noop */ }
    } else if (!doc && type === "kontrak_produksi") {
      try { sections = (await api.get("/settings/kontrak-produksi-sections")).data; } catch { /* noop */ }
    } else if (!doc && type.startsWith("doctype_")) {
      try { sections = (await api.get(`/document-types/${type}`)).data.sections; } catch { /* noop */ }
    }
    const revenue_kind = type === "kontrak_design" ? "design" : type === "kontrak_produksi" ? "produksi" : f.revenue_kind;
    setF({ ...f, type, number, payment: { ...f.payment, status }, sections, revenue_kind });
  };

  // "Cakupan" only matters at creation — picks which Kontrak Design variant's default
  // sections to start from ("full" = to Shop Drawing/RAB, "3d" = stops at Rendering
  // Final), and pre-fills the matching standard termin split. Both stay fully editable
  // afterward — this is just a sane starting point, not a lock-in.
  const changeCakupan = async (variant) => {
    let sections = f.sections;
    try { sections = (await api.get(`/settings/kontrak-design-sections?variant=${variant}`)).data; } catch { /* noop */ }
    setF({ ...f, cakupan: variant, sections });
  };

  const applyTerminPreset = (preset) => {
    const rows = preset === "full"
      ? [["Down Payment (Desain Concept)", 40], ["Termin 2 (Desain Rendering)", 40], ["Pelunasan (Shop Drawing & RAB)", 20]]
      : [["Down Payment (Desain Concept)", 50], ["Pelunasan (Desain Rendering)", 50]];
    setF((s) => ({ ...s, termins: rows.map(([label, pct]) => ({ label, pct })) }));
  };

  // Production termin templates carry a per-termin trigger/mechanism note (release
  // condition), unlike Design's plain % split — still fully editable afterward, this
  // is just a sane starting point matching Adari's own standard patterns.
  const PRODUKSI_TERMIN_PRESETS = {
    residential: [
      ["Down Payment", 30, "Dicairkan pada saat penandatanganan kontrak dan Form Persetujuan Desain Final (DF-01)."],
      ["Progres 1", 35, "Dicairkan setelah progres pekerjaan mencapai 40%."],
      ["Progres 2", 25, "Dicairkan setelah progres pekerjaan mencapai 80%."],
      ["Pelunasan", 10, "Dicairkan pada saat serah terima 100% dan Berita Acara Serah Terima (BAST) ditandatangani."],
    ],
    event: [
      ["Down Payment", 50, "Dicairkan pada saat penandatanganan kontrak dan Form Persetujuan Desain Final (DF-01)."],
      ["Termin II", 40, "Dicairkan setelah progres fabrikasi mencapai 60%, dengan jatuh tempo paling lambat 7 hari sebelum pemasangan (loading-in)."],
      ["Pelunasan", 10, "Dicairkan paling lambat 3 hari setelah acara/event berakhir."],
    ],
    progresif: [
      ["Down Payment", 30, "Dicairkan pada saat penandatanganan kontrak dan Form Persetujuan Desain Final (DF-01)."],
      ["Termin II", 40, "Dicairkan setelah progres pekerjaan mencapai 35%."],
      ["Termin III", 25, "Dicairkan setelah progres pekerjaan mencapai 80%."],
      ["Pelunasan", 5, "Dicairkan setelah pekerjaan selesai 100% dan Berita Acara Serah Terima (BAST) ditandatangani."],
    ],
  };
  const applyProduksiTerminPreset = (preset) => {
    setF((s) => ({ ...s, termins: PRODUKSI_TERMIN_PRESETS[preset].map(([label, pct, note]) => ({ label, pct, note })) }));
  };

  const changeKind = (kind) => {
    const status = f.type === "invoice" ? computeInvoiceStatus(lead, kind) : f.payment.status;
    setF({ ...f, revenue_kind: kind, payment: { ...f.payment, status } });
  };

  if (!f) return null;
  const isInv = f.type === "invoice";
  const isFreeDoc = f.type === "dokumen_lain" || f.type?.startsWith("doctype_");
  const hasSections = f.type === "quotation" || f.type === "kontrak_design" || f.type === "kontrak_produksi" || isFreeDoc;
  const isProdContract = f.type === "kontrak_produksi";
  const isEventProject = /event|pameran/i.test(f.kategori_proyek || "");

  const setItem = (idx, k, v) => setF((s) => ({ ...s, items: s.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
  const addItem = () => setF((s) => ({ ...s, items: [...s.items, blankItem()] }));
  const delItem = (idx) => setF((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));
  const rowAmt = (it) => it.amount !== "" && it.amount != null ? Number(it.amount) : Number(it.volume || 0) * Number(it.price || 0);
  const subtotal = f.items.reduce((a, it) => a + rowAmt(it), 0);
  const discountAmt = Math.round(subtotal * Number(f.discount_pct || 0) / 100);
  const total = subtotal - discountAmt + Number(f.tax || 0);
  const toggleBank = (id) => setF((s) => ({ ...s, bank_account_ids: s.bank_account_ids.includes(id) ? s.bank_account_ids.filter((x) => x !== id) : [...s.bank_account_ids, id] }));

  const save = async () => {
    if (!isFreeDoc && !f.items.some((it) => it.description.trim())) { toast.error("Isi minimal 1 item"); return; }
    if (isFreeDoc && !f.title.trim()) { toast.error("Isi judul dokumen"); return; }
    if (isProdContract && !f.nik_klien.trim()) { toast.error("NIK Klien wajib diisi sebelum Kontrak Produksi bisa dibuat"); return; }
    setBusy(true);
    const payload = {
      ...f, lead_id: lead.lead_id, tax: Number(f.tax || 0), discount_pct: Number(f.discount_pct || 0),
      items: f.items.map((it) => ({
        description: it.description, unit: it.unit,
        volume: Number(it.volume || 0), price: Number(it.price || 0),
        amount: it.amount === "" ? null : Number(it.amount),
      })),
      termins: (f.termins || []).map((t) => ({ label: t.label, pct: Number(t.pct || 0), note: t.note || "" })),
    };
    try {
      const saved = doc ? (await api.put(`/documents/${doc.doc_id}`, payload)).data : (await api.post("/documents", payload)).data;
      toast.success("Dokumen disimpan"); qc.invalidateQueries({ queryKey: ["documents", lead.lead_id] });
      onSaved?.(saved); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const inp = "bg-[#18181B] border-white/10 h-9";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f12] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto hide-scrollbar">
        <DialogHeader>
          <DialogTitle className="font-heading">{doc ? "Edit Dokumen" : "Buat Dokumen"}</DialogTitle>
          <DialogDescription className="text-zinc-500">Semua kolom bisa diedit. Total dihitung otomatis.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {lead?.nilai_produksi > 0 && f.type !== "kontrak_design" && (
            <div>
              <Label className="text-[11px] text-zinc-400 uppercase">Untuk</Label>
              <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/10 mt-1 w-fit">
                <button type="button" data-testid="doc-kind-design" onClick={() => changeKind("design")}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium ${f.revenue_kind !== "produksi" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Design</button>
                <button type="button" data-testid="doc-kind-produksi" onClick={() => changeKind("produksi")}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium ${f.revenue_kind === "produksi" ? "bg-purple-500/20 text-purple-300" : "text-zinc-500"}`}>Produksi</button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><Label className="text-[11px] text-zinc-400 uppercase">Jenis</Label>
              <Select value={f.type} onValueChange={changeType}>
                <SelectTrigger data-testid="doc-type" className={inp + " mt-1"}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10">
                  <SelectItem value="quotation">Quotation / SPH</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="kontrak_design">Kontrak Design</SelectItem>
                  <SelectItem value="kontrak_produksi">Kontrak Produksi</SelectItem>
                  {documentTypes.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px] text-zinc-400 uppercase">Nomor</Label><Input data-testid="doc-number" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} className={inp + " mt-1"} /></div>
            <div><Label className="text-[11px] text-zinc-400 uppercase">Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp + " mt-1"} /></div>
            <div><Label className="text-[11px] text-zinc-400 uppercase">Status Bayar</Label>
              {isInv ? (
                <div className={inp + " mt-1 flex items-center px-3 text-sm font-medium " + (f.payment.status === "LUNAS" ? "text-emerald-400" : f.payment.status === "SEBAGIAN" ? "text-amber-400" : "text-zinc-400")} data-testid="doc-status-readonly">
                  {f.payment.status} <span className="text-[10px] text-zinc-600 ml-1.5 font-normal">(otomatis)</span>
                </div>
              ) : (
                <Select value={f.payment.status} onValueChange={(v) => setF({ ...f, payment: { ...f.payment, status: v } })}>
                  <SelectTrigger className={inp + " mt-1"}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10"><SelectItem value="BELUM LUNAS">BELUM LUNAS</SelectItem><SelectItem value="LUNAS">LUNAS</SelectItem><SelectItem value="DP">DP</SelectItem><SelectItem value="Termin 2">Termin 2</SelectItem><SelectItem value="Termin 3">Termin 3</SelectItem></SelectContent>
                </Select>
              )}
            </div>
          </div>

          {(f.type === "kontrak_design" || isProdContract) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {!doc && f.type === "kontrak_design" && (
                <div><Label className="text-[11px] text-zinc-400 uppercase">Cakupan Kontrak</Label>
                  <Select value={f.cakupan || "full"} onValueChange={changeCakupan}>
                    <SelectTrigger data-testid="doc-cakupan" className={inp + " mt-1"}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      <SelectItem value="full">Full (s.d. Shop Drawing &amp; RAB)</SelectItem>
                      <SelectItem value="3d">3D Only (s.d. Rendering Final)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><Label className="text-[11px] text-zinc-400 uppercase">Masa Persiapan (hari)</Label>
                  <Input data-testid="doc-persiapan-days" type="number" min={0} value={f.masa_persiapan_hari || ""}
                    onChange={(e) => setF({ ...f, masa_persiapan_hari: Number(e.target.value) || 0 })}
                    placeholder="mis. 10 (opsional)" className={inp + " mt-1"} />
                </div>
                <div><Label className="text-[11px] text-zinc-400 uppercase">Durasi Pekerjaan (hari kerja)</Label>
                  <Input data-testid="doc-duration-days" type="number" min={0} value={f.duration_days || ""}
                    onChange={(e) => setF({ ...f, duration_days: Number(e.target.value) || 0 })}
                    placeholder="mis. 30" className={inp + " mt-1"} />
                </div>
                <div><Label className="text-[11px] text-zinc-400 uppercase">Mulai Tanggal{f.masa_persiapan_hari > 0 ? " (Persiapan)" : ""}</Label>
                  <Input data-testid="doc-duration-start" type="date" value={f.duration_start_date || f.date}
                    onChange={(e) => setF({ ...f, duration_start_date: e.target.value })} className={inp + " mt-1"} />
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 -mt-1">
                Masa Persiapan (mobilisasi, pemesanan material, dll.) dihitung terpisah dari Durasi Pekerjaan — pekerjaan fisik baru mulai
                setelah Masa Persiapan selesai. Kosongkan/isi 0 kalau proyek ini tidak butuh masa persiapan terpisah.
              </p>
            </div>
          )}
          {(f.type === "kontrak_design" || isProdContract) && !!f.duration_days && (
            <p className="text-xs text-blue-300/80 -mt-1" data-testid="doc-duration-preview">
              {f.masa_persiapan_hari > 0 ? (
                <>
                  → Masa Persiapan: {fmtDurationRange(f.duration_start_date || f.date, f.masa_persiapan_hari)}
                  <br />→ Pekerjaan Fisik: {fmtDurationRange(addDaysIso(f.duration_start_date || f.date, f.masa_persiapan_hari), f.duration_days)}
                </>
              ) : (
                <>→ {fmtDurationRange(f.duration_start_date || f.date, f.duration_days)}</>
              )}
            </p>
          )}

          {isProdContract && (
            <div className="rounded-xl bg-[#18181B]/50 border border-white/5 p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label className="text-[11px] text-zinc-400 uppercase">Kategori Proyek</Label>
                  <Select value={f.kategori_proyek || ""} onValueChange={(v) => setF({ ...f, kategori_proyek: v })}>
                    <SelectTrigger data-testid="doc-kategori-proyek" className={inp + " mt-1"}><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      {(master?.kategori_customer || []).map((m) => <SelectItem key={m.master_id || m.value} value={m.value}>{m.value}</SelectItem>)}
                      {!(master?.kategori_customer || []).some((m) => /event|pameran/i.test(m.value)) && <SelectItem value="Event/Pameran">Event/Pameran</SelectItem>}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-600 mt-1 leading-relaxed">
                    Hanya berpengaruh ke Pasal 1: kalau kategorinya <b>Event/Pameran</b>, muncul kolom Tanggal Pelaksanaan &amp; Pembongkaran di bawah ini.
                    Kategori lain (Residential, dll.) tidak menambah apa pun ke isi kontrak. Ini <b>tidak</b> otomatis mengubah template termin —
                    pilih sendiri template termin yang sesuai di bagian bawah (Residential/Event/Progresif).
                  </p>
                </div>
                <div><Label className="text-[11px] text-zinc-400 uppercase">NIK Klien <span className="text-red-400">*</span></Label>
                  <Input data-testid="doc-nik-klien" value={f.nik_klien} onChange={(e) => setF({ ...f, nik_klien: e.target.value })} placeholder="Wajib diisi" className={inp + " mt-1"} />
                </div>
              </div>

              {isEventProject && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px] text-zinc-400 uppercase">Tanggal Pelaksanaan Event</Label>
                    <Input data-testid="doc-event-tanggal" type="date" value={f.event_tanggal_pelaksanaan} onChange={(e) => setF({ ...f, event_tanggal_pelaksanaan: e.target.value })} className={inp + " mt-1"} />
                  </div>
                  <div><Label className="text-[11px] text-zinc-400 uppercase">Tanggal Pembongkaran</Label>
                    <Input data-testid="doc-event-bongkar" type="date" value={f.event_tanggal_bongkar} onChange={(e) => setF({ ...f, event_tanggal_bongkar: e.target.value })} className={inp + " mt-1"} />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input type="checkbox" id="doc-kena-ppn" data-testid="doc-kena-ppn" checked={f.kena_ppn} onChange={(e) => {
                  const on = e.target.checked;
                  // Kena PPN now actually drives the Tax(Rp) field (11% of subtotal) instead of
                  // just printing a promise in the contract text — same tax/total math the
                  // Invoice/SPH forms already use, so a document generated off this contract's
                  // total stays consistent instead of silently ignoring PPN.
                  setF((s) => ({ ...s, kena_ppn: on, tax: on ? Math.round(subtotal * 0.11) : 0 }));
                }} className="accent-blue-600 w-4 h-4" />
                <label htmlFor="doc-kena-ppn" className="text-sm text-zinc-300">Kena PPN 11% (otomatis dihitung dari nilai kontrak)</label>
              </div>

              <div>
                <Label className="text-[11px] text-zinc-400 uppercase">Denda Keterlambatan Pembayaran</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <div><label className="text-[10px] text-zinc-500">% per hari</label><Input data-testid="doc-denda-pct" type="number" step="0.01" value={f.denda_pct_per_hari} onChange={(e) => setF({ ...f, denda_pct_per_hari: Number(e.target.value) || 0 })} className={inp} /></div>
                  <div><label className="text-[10px] text-zinc-500">Maks. denda (%)</label><Input data-testid="doc-denda-maks" type="number" step="0.5" value={f.denda_maks_pct} onChange={(e) => setF({ ...f, denda_maks_pct: Number(e.target.value) || 0 })} className={inp} /></div>
                  <div><label className="text-[10px] text-zinc-500">Toleransi (hari)</label><Input data-testid="doc-toleransi-hari" type="number" value={f.toleransi_hari} onChange={(e) => setF({ ...f, toleransi_hari: Number(e.target.value) || 0 })} className={inp} /></div>
                </div>
              </div>

              <div>
                <Label className="text-[11px] text-zinc-400 uppercase">Masa Garansi</Label>
                <Select value={garansiCustom ? "custom" : (GARANSI_PRESETS.includes(f.garansi_hari) ? String(f.garansi_hari) : "custom")}
                  onValueChange={(v) => { setGaransiCustom(v === "custom"); if (v !== "custom") setF({ ...f, garansi_hari: Number(v) }); }}>
                  <SelectTrigger data-testid="doc-garansi-preset" className={inp + " mt-1"}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    <SelectItem value="30">30 hari</SelectItem>
                    <SelectItem value="60">60 hari</SelectItem>
                    <SelectItem value="90">90 hari (3 bulan)</SelectItem>
                    <SelectItem value="180">6 bulan</SelectItem>
                    <SelectItem value="365">12 bulan</SelectItem>
                    <SelectItem value="custom">Custom / Manual</SelectItem>
                  </SelectContent>
                </Select>
                {(garansiCustom || !GARANSI_PRESETS.includes(f.garansi_hari)) && (
                  <Input data-testid="doc-garansi-custom" type="number" min={0} value={f.garansi_hari} onChange={(e) => setF({ ...f, garansi_hari: Number(e.target.value) || 0 })}
                    placeholder="Jumlah hari" className={inp + " mt-2"} />
                )}
                <p className="text-[10px] text-zinc-600 mt-1.5 leading-relaxed">
                  Acuan umum (bukan aturan baku, sesuaikan dengan kebijakan/kesepakatan): nilai kontrak {"<"} Rp 100 juta → 30 hari · {"<"} Rp 300 juta → 60 hari · {"<"} Rp 500 juta → 90 hari (3 bulan) · {"<"} Rp 1 miliar → 6 bulan · {"<"} Rp 3 miliar → 12 bulan.
                  {total > 0 && <> Saran untuk nilai kontrak saat ini (<b>{rupiah(total)}</b>): <b>{suggestedGaransiLabel(total)}</b>.</>}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px] text-zinc-400 uppercase">Head Designer</Label>
                  <Input data-testid="doc-head-designer" value={f.head_designer} onChange={(e) => setF({ ...f, head_designer: e.target.value })} className={inp + " mt-1"} />
                </div>
                <div><Label className="text-[11px] text-zinc-400 uppercase">Project Manager</Label>
                  <Input data-testid="doc-project-manager" value={f.project_manager} onChange={(e) => setF({ ...f, project_manager: e.target.value })} className={inp + " mt-1"} />
                </div>
              </div>
            </div>
          )}

          {isFreeDoc && (
            <div><Label className="text-[11px] text-zinc-400 uppercase">Judul Dokumen</Label>
              <Input data-testid="doc-free-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                placeholder="mis. Surat Pernyataan Serah Terima" className={inp + " mt-1"} />
            </div>
          )}

          <details className="rounded-xl bg-[#18181B]/50 border border-white/5 p-3">
            <summary className="text-sm font-medium cursor-pointer">Header Perusahaan (bisa diedit)</summary>
            <p className="text-[11px] text-zinc-500 mt-1 mb-2">Logo otomatis dari Admin &gt; Logo Dokumen, tidak perlu diisi di sini.</p>
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.company.name} onChange={(e) => setF({ ...f, company: { ...f.company, name: e.target.value } })} placeholder="Nama" className={inp} />
              <Input value={f.company.phone} onChange={(e) => setF({ ...f, company: { ...f.company, phone: e.target.value } })} placeholder="Telp" className={inp} />
              <Input value={f.company.email} onChange={(e) => setF({ ...f, company: { ...f.company, email: e.target.value } })} placeholder="Email" className={inp} />
              <Input value={f.company.subtitle} onChange={(e) => setF({ ...f, company: { ...f.company, subtitle: e.target.value } })} placeholder="Subtitle" className={inp} />
              <Input value={f.company.address} onChange={(e) => setF({ ...f, company: { ...f.company, address: e.target.value } })} placeholder="Alamat" className={inp + " col-span-2"} />
            </div>
          </details>

          <div className="grid grid-cols-2 gap-2">
            <Input value={f.client.nama} onChange={(e) => setF({ ...f, client: { ...f.client, nama: e.target.value } })} placeholder="Nama Client" className={inp} data-testid="doc-client-nama" />
            <Input value={f.client.telp} onChange={(e) => setF({ ...f, client: { ...f.client, telp: e.target.value } })} placeholder="Telp Client" className={inp} />
            <Input value={f.client.email} onChange={(e) => setF({ ...f, client: { ...f.client, email: e.target.value } })} placeholder="Email Client" className={inp} />
            <Input value={f.client.alamat} onChange={(e) => setF({ ...f, client: { ...f.client, alamat: e.target.value } })} placeholder="Alamat Client" className={inp} />
          </div>

          {!isFreeDoc && <div>
            {isProdContract && (
              <label className="flex items-center gap-2 mb-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={f.sertakan_rincian_rab !== false} onChange={(e) => {
                  const on = e.target.checked;
                  if (!on) {
                    // Switching to "RAB terlampir" mode: collapse whatever items exist into a
                    // single total the user types directly — the contract text (already toggled
                    // off by sertakan_rincian_rab) then just references the attached RAB instead
                    // of listing rows, so there's nothing left to itemize here either.
                    setF((s) => ({ ...s, sertakan_rincian_rab: false, items: [{ description: "Nilai Kontrak (rincian sesuai RAB terlampir)", volume: 1, unit: "ls", price: subtotal || 0, amount: subtotal || 0 }] }));
                  } else {
                    setF((s) => ({ ...s, sertakan_rincian_rab: true, items: [blankItem()] }));
                  }
                }} className="accent-blue-600" />
                Cantumkan rincian item (uraian/volume/harga) di dalam kontrak
              </label>
            )}
            {isProdContract && f.sertakan_rincian_rab === false ? (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-400 shrink-0">Nilai Kontrak (Rp)</Label>
                <NumberInput value={f.items[0]?.price || 0} onChange={(e) => {
                  const val = Number(e.target.value || 0);
                  setF((s) => ({ ...s, items: [{ description: "Nilai Kontrak (rincian sesuai RAB terlampir)", volume: 1, unit: "ls", price: val, amount: val }] }));
                }} className={inp + " text-right flex-1"} />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2"><Label className="text-xs uppercase text-zinc-400">Item {isInv ? "Tagihan" : "Pekerjaan"}</Label>
                  <Button data-testid="doc-add-item" onClick={addItem} variant="outline" size="sm" className="h-8 bg-[#18181B] border-white/10"><Plus size={14} className="mr-1" />Baris</Button>
                </div>
                <div className="overflow-x-auto hide-scrollbar">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead><tr className="text-[11px] text-zinc-500 uppercase">
                      <th className="text-left pb-1">Deskripsi</th>
                      <th className="pb-1 w-16">Vol</th><th className="pb-1 w-16">Unit</th><th className="pb-1 w-28">Harga</th><th className="pb-1 w-28 text-right">Jumlah</th>
                      <th></th>
                    </tr></thead>
                    <tbody>
                      {f.items.map((it, idx) => (
                        <tr key={idx} data-testid={`doc-item-${idx}`}>
                          <td className="pr-1 py-1"><Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className={inp} placeholder={isInv ? "Biaya Desain…" : "Biaya Desain…"} /></td>
                          <td className="px-1"><Input type="number" value={it.volume} onChange={(e) => setItem(idx, "volume", e.target.value)} className={inp + " text-right"} /></td>
                          <td className="px-1"><Input value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)} className={inp} /></td>
                          <td className="px-1"><NumberInput value={it.price} onChange={(e) => setItem(idx, "price", e.target.value)} className={inp + " text-right"} /></td>
                          <td className="pl-1 text-right text-emerald-400 font-medium whitespace-nowrap tabular-nums">{rupiah(rowAmt(it))}</td>
                          <td><button onClick={() => delItem(idx)} className="text-red-400 p-1"><Trash2 size={14} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>}

          {hasSections && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase text-zinc-400">{(f.type === "kontrak_design" || isProdContract) ? "Pasal Kontrak" : "Bagian Naskah (Ruang Lingkup, Revisi, dll.)"}</Label>
                <Button type="button" onClick={() => setF((s) => ({ ...s, sections: [...(s.sections || []), { id: `sec_${Date.now()}`, title: (f.type === "kontrak_design" || isProdContract) ? "Pasal Baru" : "Bagian Baru", body: "", kind: "text" }] }))} variant="outline" size="sm" className="h-8 bg-[#18181B] border-white/10"><Plus size={14} className="mr-1" />{(f.type === "kontrak_design" || isProdContract) ? "Pasal" : "Bagian"}</Button>
              </div>
              <div className="space-y-2">
                {(f.sections || []).map((s, i) => (
                  <div key={s.id || i} className="rounded-lg bg-[#18181B] border border-white/5 p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Input data-testid={`doc-section-title-${i}`} value={s.title} onChange={(e) => setF((st) => ({ ...st, sections: st.sections.map((x, j) => j === i ? { ...x, title: e.target.value } : x) }))} placeholder="Judul bagian" className={inp + " flex-1"} />
                      {AUTO_SECTION_KINDS.includes(s.kind) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 shrink-0 whitespace-nowrap">Sebagian Otomatis</span>}
                      <button type="button" onClick={() => setF((st) => ({ ...st, sections: st.sections.filter((_, j) => j !== i) }))} className="text-red-400 p-1.5"><Trash2 size={14} /></button>
                    </div>
                    {s.kind === "biaya" || s.kind === "biaya_termin" ? (
                      <p className="text-xs text-zinc-600 italic">Isinya otomatis dari Item &amp; Termin Pembayaran di dokumen ini.</p>
                    ) : (
                      <Textarea data-testid={`doc-section-body-${i}`} value={s.body} onChange={(e) => setF((st) => ({ ...st, sections: st.sections.map((x, j) => j === i ? { ...x, body: e.target.value } : x) }))} rows={4} placeholder="Isi paragraf…" className="bg-[#0f0f12] border-white/10 text-sm" />
                    )}
                  </div>
                ))}
                {(f.sections || []).length === 0 && <p className="text-xs text-zinc-600 italic">Tidak ada bagian tambahan di dokumen ini.</p>}
              </div>
            </div>
          )}

          {!isFreeDoc && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div className="space-y-2">
              {isProdContract && f.kena_ppn ? (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-zinc-400 w-24">PPN 11%</Label>
                  <div className={inp + " w-36 flex items-center text-emerald-400"}>{rupiah(f.tax || 0)}</div>
                  <span className="text-[10px] text-zinc-600">otomatis, ikut checkbox Kena PPN di atas</span>
                </div>
              ) : (() => {
                const isKontrakType = f.type === "kontrak_design" || isProdContract;
                const discountTaxVisible = !isKontrakType || showDiscountTax || Number(f.discount_pct) > 0 || Number(f.tax) > 0;
                if (!discountTaxVisible) {
                  return (
                    <button type="button" onClick={() => setShowDiscountTax(true)} className="text-[11px] text-blue-400 hover:text-blue-300 underline underline-offset-2">
                      + Tambahkan Discount/Pajak (opsional)
                    </button>
                  );
                }
                return (
                  <>
                    <div className="flex items-center gap-2"><Label className="text-xs text-zinc-400 w-24">Discount %</Label><Input type="number" value={f.discount_pct} onChange={(e) => setF({ ...f, discount_pct: e.target.value })} className={inp + " w-24"} /></div>
                    <div className="flex items-center gap-2"><Label className="text-xs text-zinc-400 w-24">Tax (Rp)</Label><NumberInput value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} className={inp + " w-36"} /></div>
                  </>
                );
              })()}
              <div className="flex items-center justify-between"><Label className="text-xs uppercase text-zinc-400">{isInv ? "Termin Pembayaran (DP, dll)" : "Termin (opsional)"}</Label><Button data-testid="doc-add-termin" onClick={() => setF((s) => ({ ...s, termins: [...(s.termins || []), { label: "", pct: 0 }] }))} variant="outline" size="sm" className="h-7 bg-[#18181B] border-white/10"><Plus size={13} /></Button></div>
              {f.type === "kontrak_design" && (
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" data-testid="termin-preset-full" onClick={() => applyTerminPreset("full")} className="text-[11px] px-2 py-1 rounded-lg bg-[#18181B] border border-white/10 text-zinc-400 hover:text-white">Standar Design Full (40-40-20)</button>
                  <button type="button" data-testid="termin-preset-3d" onClick={() => applyTerminPreset("3d")} className="text-[11px] px-2 py-1 rounded-lg bg-[#18181B] border border-white/10 text-zinc-400 hover:text-white">Standar 3D Only (50-50)</button>
                </div>
              )}
              {isProdContract && (
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" data-testid="termin-preset-residential" onClick={() => applyProduksiTerminPreset("residential")} className="text-[11px] px-2 py-1 rounded-lg bg-[#18181B] border border-white/10 text-zinc-400 hover:text-white">Template Residential (30-35-25-10)</button>
                  <button type="button" data-testid="termin-preset-event" onClick={() => applyProduksiTerminPreset("event")} className="text-[11px] px-2 py-1 rounded-lg bg-[#18181B] border border-white/10 text-zinc-400 hover:text-white">Template Event/Pameran (50-40-10)</button>
                  <button type="button" data-testid="termin-preset-progresif" onClick={() => applyProduksiTerminPreset("progresif")} className="text-[11px] px-2 py-1 rounded-lg bg-[#18181B] border border-white/10 text-zinc-400 hover:text-white">Template Progresif (30-40-25-5)</button>
                </div>
              )}
              {(f.termins || []).map((t, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex gap-2 items-center">
                    <Input value={t.label} onChange={(e) => setF((s) => ({ ...s, termins: s.termins.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) }))} placeholder="Down Payment" className={inp} />
                    <Input type="number" value={t.pct} onChange={(e) => setF((s) => ({ ...s, termins: s.termins.map((x, i) => i === idx ? { ...x, pct: e.target.value } : x) }))} placeholder="%" className={inp + " w-16"} />
                    <span className="text-xs text-emerald-400 w-24 text-right whitespace-nowrap tabular-nums">{rupiah(Math.round(total * Number(t.pct || 0) / 100))}</span>
                    <button onClick={() => setF((s) => ({ ...s, termins: s.termins.filter((_, i) => i !== idx) }))} className="text-red-400"><Trash2 size={13} /></button>
                  </div>
                  {isProdContract && (
                    <Input value={t.note || ""} onChange={(e) => setF((s) => ({ ...s, termins: s.termins.map((x, i) => i === idx ? { ...x, note: e.target.value } : x) }))}
                      placeholder="Keterangan mekanisme/trigger pembayaran (opsional)" className={inp + " text-xs"} data-testid={`termin-note-${idx}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="card-surface rounded-xl p-3 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-zinc-500">Sub Total</span><span className="whitespace-nowrap tabular-nums">{rupiah(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Discount</span><span className="text-red-400 whitespace-nowrap tabular-nums">- {rupiah(discountAmt)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span className="whitespace-nowrap tabular-nums">{rupiah(Number(f.tax || 0))}</span></div>
              <div className="flex justify-between font-bold border-t border-white/10 pt-1.5"><span>Grand Total</span><span className="text-emerald-400 whitespace-nowrap tabular-nums" data-testid="doc-total">{rupiah(total)}</span></div>
            </div>
          </div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label className="text-[11px] text-zinc-400 uppercase">Alamat Cabang di Dokumen</Label>
              <Select value={f.address_id || "none"} onValueChange={(v) => setF({ ...f, address_id: v === "none" ? "" : v })}>
                <SelectTrigger className={inp + " mt-1"}><SelectValue placeholder="Pilih alamat" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10">
                  <SelectItem value="none">- Tidak ditampilkan -</SelectItem>
                  {companyAddresses.map((a) => <SelectItem key={a.address_id} value={a.address_id}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px] text-zinc-400 uppercase">Tanda Tangan</Label>
              <Select value={f.signature_id || "none"} onValueChange={(v) => setF({ ...f, signature_id: v === "none" ? "" : v })}>
                <SelectTrigger className={inp + " mt-1"}><SelectValue placeholder="Pilih TTD" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10">
                  <SelectItem value="none">- Tidak pakai TTD tersimpan -</SelectItem>
                  {companySignatures.map((s) => <SelectItem key={s.signature_id} value={s.signature_id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400 uppercase">Rekening Ditampilkan</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {bankAccounts.map((b) => (
                <button key={b.bank_account_id} type="button" onClick={() => toggleBank(b.bank_account_id)}
                  className={"px-3 py-1.5 rounded-full text-xs border " + (f.bank_account_ids.includes(b.bank_account_id) ? "bg-blue-600/20 border-blue-500 text-blue-300" : "bg-[#18181B] border-white/10 text-zinc-400")}>
                  {b.label}
                </button>
              ))}
              {!bankAccounts.length && <span className="text-xs text-zinc-500">Belum ada rekening — tambah di Admin &gt; Rekening.</span>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Input value={f.signer_name} onChange={(e) => setF({ ...f, signer_name: e.target.value })} placeholder="Nama Penandatangan" className={inp} />
            <Input value={f.signer_title} onChange={(e) => setF({ ...f, signer_title: e.target.value })} placeholder="Jabatan Penandatangan" className={inp} />
          </div>
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Catatan (opsional)" rows={2} className="bg-[#18181B] border-white/10" />

          <Button data-testid="doc-save" onClick={save} disabled={busy} className="w-full h-11 bg-gradient-to-r from-blue-700 to-blue-900 font-semibold">{busy ? <Loader2 className="animate-spin" size={18} /> : "Simpan Dokumen"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
